import { Box, Flex, Text } from '@chakra-ui/react';
import { ChatAvatar } from '@/components/chat/ChatAvatar';
import { formatChatTime } from '../formatChatTime';
import type { ChatListItem } from '../useCommunicationPage';
import { MutedBell } from './MutedBell';
import { UnreadBadge } from './UnreadBadge';

/**
 * Left inset of the row separator, so it starts under the text rather than the avatar --
 * the standard iOS list treatment. = row padding (16) + avatar column (44) + gap (12).
 */
const ROW_SEPARATOR_INSET = '72px';

type Props = {
  item: ChatListItem;
  active: boolean;
  onSelect: () => void;
};

/** One private thread. Keeps the row anatomy the flat list had before the redesign. */
export function PrivateMessageRow({ item, active, onSelect }: Props) {
  return (
    <Flex
      role="button"
      tabIndex={0}
      align="center"
      gap={3}
      px={4}
      // Taller rows on touch: bigger tap target and more air between chats.
      py={{ base: 3.5, lg: 4 }}
      position="relative"
      cursor="pointer"
      bg={active ? 'blue.50' : 'transparent'}
      borderRightWidth={active ? 4 : 0}
      borderColor="blue.500"
      _hover={active ? {} : { bg: 'gray.100' }}
      // Touch has no hover, so give the press itself visible feedback.
      _active={{ bg: active ? 'blue.50' : 'gray.200' }}
      transition="background 0.12s ease-out"
      // Hairline between rows, inset to the text. A pseudo-element rather than a border so
      // it sits at the row's edge and can be dropped on the final row without leaving a
      // dangling line above the empty space.
      _after={{
        content: '""',
        position: 'absolute',
        left: ROW_SEPARATOR_INSET,
        right: 0,
        bottom: 0,
        height: '1px',
        bg: 'volyx.borderLight',
      }}
      _last={{ _after: { display: 'none' } }}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
    >
      {/* Fixed-width avatar column keeps every row's text on the same x, and makes
          ROW_SEPARATOR_INSET exact rather than a guess at avatar size. */}
      <Flex w="44px" flexShrink={0} justify="center">
        <ChatAvatar kind="direct" stored={item.image} name={item.title} size="md" />
      </Flex>
      <Box flex={1} minW={0}>
        <Flex justify="space-between" align="baseline" mb={1} gap={2}>
          <Text
            fontSize="sm"
            fontWeight={item.unreadCount > 0 ? 'bold' : 'semibold'}
            color="gray.800"
            lineClamp={1}
          >
            {item.title}
          </Text>
          <Text
            fontSize="xs"
            // The count badge is the row's only coloured signal now. A blue timestamp
            // beside it read as a second, competing one.
            color="gray.400"
            fontWeight={item.unreadCount > 0 ? 'semibold' : 'normal'}
            flexShrink={0}
          >
            {formatChatTime(item.lastMessageAt ?? item.createdAt)}
          </Text>
        </Flex>
        <Flex align="center" gap={2}>
          <Text
            fontSize="sm"
            // The ask: unread previews are bold. Darkened too, because bold grey on white
            // still reads as muted.
            fontWeight={item.unreadCount > 0 ? 'semibold' : 'normal'}
            color={item.unreadCount > 0 ? 'gray.800' : 'gray.500'}
            lineClamp={1}
            flex={1}
            minW={0}
          >
            {item.preview}
          </Text>
          {item.muted ? <MutedBell /> : null}
          <UnreadBadge count={item.unreadCount} muted={item.muted} />
        </Flex>
      </Box>
    </Flex>
  );
}
